/**
 * App filesystem confinement (SUP-181) — the parent-side guard that keeps a
 * sandboxed App's `window.playground` calls inside its own space. The App
 * reaches the FS only via parent RPC, so a guard applied at the RPC dispatch
 * boundary (see `dispatch.ts`) is a real boundary, not advisory.
 *
 * EVERY app is confined; confinement has no trusted/workspace mode. An app's
 * reachable space is its install dir + its canonical data dir + the grants the
 * HOST supplies — and the host must only supply grants its consent store holds
 * (never the app's own manifest ask; an app that can edit its manifest could
 * otherwise un-confine itself). A host-side pre-approval (Hearth's `trusted`
 * marker for built-ins) changes what the consent store answers, not this
 * boundary. Workspace-wide reach is just an approved grant on `"/"`, not a
 * special mode.
 *
 * Enforcement is the shared policy engine's `fs` domain: the app's reachable
 * subtrees become allow rules; any path outside them is a default-deny and the
 * RPC throws.
 */
import { invariant } from "ts-invariant";

import { fsDomain } from "../policy/domains/fs";
import { compile, decide } from "../policy/engine";
import type { CompiledPolicy } from "../policy/engine";
import type { PolicyRule } from "../policy/types";

/** One approved widening of an app's reach: a workspace path (covering its
 *  subtree), read-only unless it explicitly asked for `readwrite`. */
export interface AppFsGrant {
  path: string;
  access?: "read" | "readwrite";
}

/** How much of the workspace an app may touch. */
export interface AppFsScope {
  /** The app's install directory (its identity path), e.g. `Apps/kanban`. */
  root: string;
  /** The app's canonical data directory (see `appDataDir`), when it has an
   *  identity to derive one from. */
  dataDir?: string;
  /** HOST-APPROVED grants beyond the app's own space — never the manifest's
   *  self-declared ask. */
  grants?: readonly AppFsGrant[];
}

/** What an RPC does to its path — the dispatcher classifies each method. */
export type FsAction = "read" | "write";

/** A guard the RPC dispatcher calls before touching `path`; throws if denied.
 *  `action` distinguishes a read from a mutate so a read-only grant can't be
 *  written through. */
export type FsGuard = (path: string, action: FsAction) => void;

const APP = "app:sandboxed" as const;

/** A grant path as a subtree pattern: "designs" → "designs/", "/" stays.
 *  Subtree-of-a-file still exact-matches the file itself, so a single-file
 *  grant needs no separate spelling. */
function subtree(path: string): string {
  const trimmed = path.replace(/\/+$/, "");
  return trimmed === "" ? "/" : `${trimmed}/`;
}

/**
 * Build the confinement policy. The app's own space (install dir + data dir)
 * and `readwrite` grants get full access (`owner`); `read` grants get
 * `viewer` (read/list only), so a read grant can never be written or deleted
 * through (codex chunk-3 review).
 */
function scopePolicy(scope: AppFsScope): CompiledPolicy {
  // An empty root would lower to the whole-workspace subtree "/" — the one
  // widening that must only ever arrive as an explicit, member-approved grant.
  invariant(
    scope.root.replace(/\/+$/, "") !== "",
    "app FS scope requires a non-empty install dir",
  );
  const owned: string[] = [subtree(scope.root)];
  if (scope.dataDir) owned.push(subtree(scope.dataDir));
  const readonly: string[] = [];
  for (const g of scope.grants ?? []) {
    // Default a grant to read-only (least privilege) unless it asked for write.
    (g.access === "readwrite" ? owned : readonly).push(subtree(g.path));
  }

  const rules: PolicyRule[] = [
    {
      id: "app-fs-owned",
      principals: ["app:*"],
      domain: "fs",
      resources: owned,
      actions: ["owner"], // read + write within the app's own space
      effect: "allow",
    },
  ];
  if (readonly.length > 0) {
    rules.push({
      id: "app-fs-readonly-grants",
      principals: ["app:*"],
      domain: "fs",
      resources: readonly,
      actions: ["viewer"], // read/list only — writes fall through to deny
      effect: "allow",
    });
  }
  return compile([fsDomain], { schemaVersion: 1, rules });
}

/**
 * The guard for `scope`. Denies any path/action the app's rules don't cover —
 * including a whole-workspace `listFiles()` (the root path) and a write to a
 * read-only grant.
 */
export function makeAppFsGuard(scope: AppFsScope): FsGuard {
  const policy = scopePolicy(scope);
  return (path: string, action: FsAction) => {
    // An empty/undefined path means the workspace root — a real resource (a
    // root `listFiles()`), which only a "/" grant covers.
    const ref = path && path.length > 0 ? path : "/";
    const allowed =
      decide(policy, { chain: [APP], domain: "fs", ref, action }).effect ===
      "allow";
    if (!allowed) {
      throw new Error(
        `This app cannot ${action} "${path}" — it is confined to its own ` +
          `folder and the paths a member has granted it.`,
      );
    }
  };
}
